import { createContext, useContext, useEffect, useState } from "react";
import type { ComponentType, ReactNode } from "react";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ApartmentIcon from "@mui/icons-material/Apartment";
import ScoreboardIcon from "@mui/icons-material/Scoreboard";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import PaymentsIcon from "@mui/icons-material/Payments";
import DescriptionIcon from "@mui/icons-material/Description";
import BadgeIcon from "@mui/icons-material/Badge";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import SchoolIcon from "@mui/icons-material/School";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import EventIcon from "@mui/icons-material/Event";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import AssignmentReturnedIcon from "@mui/icons-material/AssignmentReturned";
import GroupIcon from "@mui/icons-material/Group";
import ContactPageIcon from "@mui/icons-material/ContactPage";
import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import request from "./request.ts";

export interface Menu {
  id: string;
  menu_name: string;
  route: string;
  icon: string;
  sort_order: number;
  status: boolean;
}

export interface AuthUser {
  id: number;
  name: string;
  username: string;
  isSuperAdmin: boolean;
  status: boolean;
}

const iconMap: Record<string, ComponentType> = {
  Dashboard: DashboardIcon,
  AdminPanelSettings: AdminPanelSettingsIcon,
  Apartment: ApartmentIcon,
  Scoreboard: ScoreboardIcon,
  PhotoLibrary: PhotoLibraryIcon,
  Payments: PaymentsIcon,
  Description: DescriptionIcon,
  Badge: BadgeIcon,
  HomeWork: HomeWorkIcon,
  School: SchoolIcon,
  EmojiEvents: EmojiEventsIcon,
  Event: EventIcon,
  MilitaryTech: MilitaryTechIcon,
  Newspaper: NewspaperIcon,
  DirectionsBus: DirectionsBusIcon,
  AssignmentReturned: AssignmentReturnedIcon,
  Group: GroupIcon,
  ContactPage: ContactPageIcon,
  SettingsApplications: SettingsApplicationsIcon,
  MenuBook: MenuBookIcon,
};

export const getMenuIcon = (name?: string): ReactNode => {
  const Icon = (name && iconMap[name]) || MenuBookIcon;
  return <Icon />;
};

interface MenusContextValue {
  menus: Menu[];
  user: AuthUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

const MenusContext = createContext<MenusContextValue>({
  menus: [],
  user: null,
  loading: true,
  refresh: () => Promise.resolve(),
});

export function MenusProvider({ children }: { children: ReactNode }) {
  const [menus, setMenus] = useState<Menu[]>([]);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      setMenus([]);
      setUser(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await request.get<{
        status: boolean;
        message: string;
        data: { user?: AuthUser; menus?: Menu[] };
      }>("/me");
      // const list = Array.isArray(res.data?.data?.menus) ? res.data.data.menus : [];
      setMenus(res.data.data.menus || []);
      setUser(res.data.data.user || null);
    } catch {
      setMenus([]);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <MenusContext.Provider value={{ menus, user, loading, refresh }}>{children}</MenusContext.Provider>
  );
}

export const useMenus = () => useContext(MenusContext);